import { Tag, Button, Typography } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import {
  ORDER_DATE_PRESET_LABEL,
  ORDER_PAYMENT_STATE_FILTER_OPTIONS,
  ORDER_B2B_CHANNEL_FILTER_OPTIONS,
  ORDER_INVOICE_STATE_FILTER_OPTIONS,
  ORDER_GIFT_FILTER_OPTIONS,
  parseOrderAmount,
  type OrderFilterKey,
  type OrderFilterPatch,
} from '../parts/filters';
import { formatVND } from '@/utils/format';
import {
  CHANNEL_FILTER_LABEL,
  ORDER_STATUS_GROUP_LABEL,
  resolveOrderStateLabel,
} from '../parts/constants';

interface ActiveFilterTagsProps {
  filters: Record<OrderFilterKey, string>;
  onApplyFilter: (patch: OrderFilterPatch) => void;
  clearFilters: () => void;
  /** TCR-1092: để hiện tên nhân viên (không phải id thô) trên tag "Người tạo đơn". */
  staffOptions?: { label: string; value: string }[];
}

const FEE_PAYER_LABEL: Record<string, string> = {
  seller: 'Người bán',
  buyer: 'Người mua',
};

const BOOL_YES_NO_LABEL: Record<string, string> = {
  true: 'Có',
  false: 'Không',
};

/** Tag phải đọc y hệt option trong drawer, nếu không người dùng không lần ra tag do mình bật. */
const PREPAID_LABEL: Record<string, string> = {
  true: 'Đã trả trước',
  false: 'Chưa trả trước',
};

interface ActiveTag {
  key: string;
  label: string;
  value: string;
  /** Patch xoá điều kiện này — date range xoá 2 key trong MỘT lần ghi URL. */
  clear: OrderFilterPatch;
}

export function buildActiveTags(
  filters: Record<OrderFilterKey, string>,
  staffOptions: { label: string; value: string }[] = [],
): ActiveTag[] {
  const tags: ActiveTag[] = [];

  if (filters.search) {
    tags.push({ key: 'search', label: 'Tìm kiếm', value: filters.search, clear: { search: '' } });
  }
  if (filters.order_number) {
    tags.push({
      key: 'order_number',
      label: 'Mã đơn',
      value: filters.order_number,
      clear: { order_number: '' },
    });
  }
  if (filters.customer) {
    tags.push({
      key: 'customer',
      label: 'Khách hàng',
      value: filters.customer,
      clear: { customer: '' },
    });
  }
  if (filters.channel_group) {
    tags.push({
      key: 'channel_group',
      label: 'Kênh',
      value:
        CHANNEL_FILTER_LABEL[filters.channel_group as keyof typeof CHANNEL_FILTER_LABEL]
        ?? filters.channel_group,
      clear: { channel_group: '' },
    });
  }
  if (filters.status) {
    tags.push({
      key: 'status',
      label: 'Trạng thái',
      value: resolveOrderStateLabel(filters.status),
      clear: { status: '' },
    });
  }
  if (filters.status_group && filters.status_group !== 'all') {
    tags.push({
      key: 'status_group',
      label: 'Nhóm trạng thái',
      value: ORDER_STATUS_GROUP_LABEL[filters.status_group as keyof typeof ORDER_STATUS_GROUP_LABEL]
        ?? filters.status_group,
      clear: { status_group: '' },
    });
  }
  if (filters.total_from || filters.total_to) {
    const from = parseOrderAmount(filters.total_from);
    const to = parseOrderAmount(filters.total_to);
    // Chỉ có một đầu thì hiển thị đúng ngữ nghĩa "từ trở lên" / "trở xuống", đừng bịa
    // đầu còn lại thành 0 hay vô cực.
    const label =
      from !== undefined && to !== undefined
        ? `${formatVND(from)} - ${formatVND(to)}`
        : from !== undefined
          ? `từ ${formatVND(from)}`
          : `đến ${formatVND(to as number)}`;
    tags.push({
      key: 'total_range',
      label: 'Tổng tiền',
      value: label,
      clear: { total_from: '', total_to: '' },
    });
  }
  if (filters.date_preset) {
    // Preset hiện nhãn ("Tháng này") thay vì khoảng ngày đã resolve — người dùng chọn
    // preset thì muốn thấy preset, và nó tự trượt sang tháng mới.
    tags.push({
      key: 'date_range',
      label: 'Thời gian',
      value: ORDER_DATE_PRESET_LABEL[filters.date_preset] ?? filters.date_preset,
      clear: { date_preset: '' },
    });
  } else if (filters.date_from || filters.date_to) {
    tags.push({
      key: 'date_range',
      label: 'Thời gian',
      value: `${filters.date_from || 'Bắt đầu'} - ${filters.date_to || 'Hiện tại'}`,
      clear: { date_from: '', date_to: '' },
    });
  }
  if (filters.created_by) {
    tags.push({
      key: 'created_by',
      label: 'Người tạo đơn',
      value: staffOptions.find((s) => s.value === filters.created_by)?.label ?? filters.created_by,
      clear: { created_by: '' },
    });
  }
  if (filters.fee_payer) {
    tags.push({
      key: 'fee_payer',
      label: 'Bên trả phí VC',
      value: FEE_PAYER_LABEL[filters.fee_payer] ?? filters.fee_payer,
      clear: { fee_payer: '' },
    });
  }
  if (filters.has_customer_note) {
    tags.push({
      key: 'has_customer_note',
      label: 'Ghi chú KH',
      value: BOOL_YES_NO_LABEL[filters.has_customer_note] ?? filters.has_customer_note,
      clear: { has_customer_note: '' },
    });
  }
  if (filters.prepaid) {
    tags.push({
      key: 'prepaid',
      label: 'Tạm ứng',
      value: PREPAID_LABEL[filters.prepaid] ?? filters.prepaid,
      clear: { prepaid: '' },
    });
  }
  if (filters.payment_state) {
    tags.push({
      key: 'payment_state',
      label: 'Thanh toán',
      value:
        ORDER_PAYMENT_STATE_FILTER_OPTIONS.find((o) => o.value === filters.payment_state)?.label
        ?? filters.payment_state,
      clear: { payment_state: '' },
    });
  }
  if (filters.b2b_channel) {
    tags.push({
      key: 'b2b_channel',
      label: 'Kênh khách B2B',
      value:
        ORDER_B2B_CHANNEL_FILTER_OPTIONS.find((o) => o.value === filters.b2b_channel)?.label
        ?? filters.b2b_channel,
      clear: { b2b_channel: '' },
    });
  }
  // TCR-2129 — thẻ cho bộ lọc CK khác chính sách.
  if (filters.has_discount_deviation) {
    tags.push({
      key: 'has_discount_deviation',
      label: 'Chiết khấu',
      value: 'Khác chính sách',
      clear: { has_discount_deviation: '' },
    });
  }
  // TCR-2625
  if (filters.gift_filter) {
    tags.push({
      key: 'gift_filter',
      label: 'Quà tặng',
      value:
        ORDER_GIFT_FILTER_OPTIONS.find((o) => o.value === filters.gift_filter)?.label
        ?? filters.gift_filter,
      clear: { gift_filter: '' },
    });
  }
  if (filters.invoice_state) {
    tags.push({
      key: 'invoice_state',
      label: 'Hoá đơn',
      value:
        ORDER_INVOICE_STATE_FILTER_OPTIONS.find((o) => o.value === filters.invoice_state)?.label
        ?? filters.invoice_state,
      clear: { invoice_state: '' },
    });
  }

  return tags;
}

/**
 * Số điều kiện lọc theo cách NGƯỜI DÙNG đếm, không phải số filter key.
 * `activeFilterCount` của useListPageState đếm key thô nên khoảng ngày cụ thể thành 2
 * (`date_from` + `date_to`) trong khi trên màn hình chỉ là 1 tag "Thời gian" — dùng nó
 * cho FilterBar sẽ ra "Xóa bộ lọc (3)" đứng cạnh "Đang lọc (2)". Cả hai chỗ phải đếm
 * từ CÙNG một nguồn là danh sách tag.
 */
export function countActiveOrderFilters(
  filters: Record<OrderFilterKey, string>,
  staffOptions: { label: string; value: string }[] = [],
): number {
  return buildActiveTags(filters, staffOptions).length;
}

export function ActiveFilterTags({ filters, onApplyFilter, clearFilters, staffOptions = [] }: ActiveFilterTagsProps) {
  const tags = buildActiveTags(filters, staffOptions);
  if (tags.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 8,
        padding: '8px 12px',
        background: '#f5f5f5',
        borderRadius: 6,
      }}
    >
      {/* Đếm theo số tag hiển thị, KHÔNG theo activeFilterCount: date_from + date_to là
          2 filter key nhưng gộp thành 1 tag, dùng activeFilterCount sẽ lệch số. */}
      <Typography.Text type="secondary" style={{ fontSize: 13, marginRight: 4 }}>
        Đang lọc ({tags.length}):
      </Typography.Text>
      {tags.map((tag) => (
        <Tag
          key={tag.key}
          closable
          closeIcon={<CloseCircleFilled />}
          onClose={(e) => {
            e.preventDefault();
            onApplyFilter(tag.clear);
          }}
        >
          <Typography.Text type="secondary">{tag.label}:</Typography.Text> {tag.value}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={clearFilters} style={{ padding: 0, fontSize: 13 }}>
        Xoá điều kiện lọc
      </Button>
    </div>
  );
}
